import type { LicenceResponse, ProfileResponse } from '@shared/schemas';

import { EMPTY_ENRICHMENT_STATS, type EnrichmentStats } from '../../_shared/domain/enrichment';
import {
  type FileImportData,
  type FileUploadState,
  initialFileUploadState,
} from '../../_shared/domain/file-import.model';
import { createInitialTree, type FilterTreeState } from '../../_shared/domain/filter-tree';
import {
  initialListImportState,
  type ListImportState,
} from '../../_shared/domain/list-import.model';
import type { EditDraft, EditListError, LoadStatus, SaveStatus } from '../domain/edit-draft.model';

/** The paginated profile table, filled by the fetch saga. */
export interface ProfileTableState {
  rows: ProfileResponse[];
  total: number;
  /** 0-indexed page (the saga converts to the 1-indexed server page). */
  page: number;
  pageSize: number;
  loading: boolean;
}

/**
 * Redux state for the full Edit List wizard. Mirrors the Add slice's shape (draft
 * + save lifecycle + the whole profile-selection data layer) and adds the
 * edit-only concerns: the initial `load` lifecycle and the `delete` lifecycle.
 * Every field is serialisable so the whole slice can be persisted / replayed.
 */
export interface EditListState {
  draft: EditDraft;
  /**
   * The list name as loaded from the server, kept apart from `draft.name` so the
   * page title stays stable while the user renames the list.
   */
  loadedName: string;
  /**
   * The owning licence as loaded, kept apart from `draft.licenceId` so a
   * transfer can be told from a save that leaves the licence alone (#520).
   */
  loadedLicenceId: string;
  loadedLicenceName: string;
  /** Lifecycle of the initial `GET /lists/:id` load. */
  loadStatus: LoadStatus;
  step: number;
  validationErrors: Record<string, string>;
  saveStatus: SaveStatus;
  savePhase: string;
  error: EditListError | null;

  // ── Delete lifecycle (edit-only) ──
  deleting: boolean;
  deleted: boolean;

  // ── Selection (serialisable) ──
  excludeDuplicates: boolean;
  importedProfileIds: string[];
  importedListName: string;
  /** Embedded upload → map → analyze mini-flow; its output is `fileImportData`. */
  fileUpload: FileUploadState;
  fileImportData: FileImportData | null;
  enrichNew: boolean;
  enrichStale: boolean;

  // ── Filters (tree is the source of truth; serverFilters is a derived selector) ──
  filters: FilterTreeState;

  // ── Profile table (filled by the fetch saga) ──
  profiles: ProfileTableState;

  // ── Async results ──
  duplicateCount: number;
  duplicateLoading: boolean;
  enrichment: EnrichmentStats;
  enrichmentLoading: boolean;
  nlSearching: boolean;

  // ── Licence + source-list dropdowns of the "Import from existing list" step ──
  licences: LicenceResponse[];
  licencesLoading: boolean;
  listImport: ListImportState;
}

export const emptyEditDraft: EditDraft = {
  listId: '',
  licenceId: '',
  licenceName: '',
  name: '',
  startDate: '',
  endDate: '',
  scope: '',
  clientScopeDescription: '',
  origin: 'invyo',
  status: 'draft',
  methods: [],
};

/** Default page size; matches the previous `useInfiniteApi(..., 25, ...)`. */
export const DEFAULT_PAGE_SIZE = 25;

export const initialState: EditListState = {
  draft: emptyEditDraft,
  loadedName: '',
  loadedLicenceId: '',
  loadedLicenceName: '',
  loadStatus: 'idle',
  step: 0,
  validationErrors: {},
  saveStatus: 'idle',
  savePhase: '',
  error: null,

  deleting: false,
  deleted: false,

  excludeDuplicates: true,
  importedProfileIds: [],
  importedListName: '',
  fileUpload: initialFileUploadState,
  fileImportData: null,
  enrichNew: false,
  enrichStale: false,

  filters: createInitialTree(),

  profiles: {
    rows: [],
    total: 0,
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    loading: false,
  },

  duplicateCount: 0,
  duplicateLoading: false,
  enrichment: EMPTY_ENRICHMENT_STATS,
  enrichmentLoading: false,
  nlSearching: false,

  licences: [],
  licencesLoading: false,
  listImport: initialListImportState,
};
